import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  HEARTBEATS, SONDES, dateCourte, depuis, fenetreLisible, getAppels,
} from '@/shared/api/telemetrie';

/**
 * Une intégration : sa sonde technique, son heartbeat métier, ses derniers appels.
 *
 * L'ORDRE DE LECTURE EST DÉLIBÉRÉ. Le heartbeat métier occupe la ligne la plus visible,
 * la sonde technique n'est qu'une pastille en en-tête. C'est l'inverse de ce qu'on écrit
 * spontanément, et c'est le point de toute la page : un service qui répond 200 en ne
 * produisant plus rien reste muet, et c'est cette panne-là qui coûte des jours.
 *
 * Les vingt derniers appels ne sont chargés qu'à l'ouverture du volet. Les charger pour
 * les six cartes d'entrée ferait six requêtes pour des listes que personne ne regarde.
 */

const pastille = (table, cle) => table[cle] || { label: cle, teinte: 'bg-slate-100 text-slate-700' };

function Appels({ journaux }) {
  if (!journaux?.length) return null;
  return journaux.map(({ service, appels }) => (
    <div key={service} className="mt-3">
      <p className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">{service}</p>
      {appels.length === 0 ? (
        <p className="text-sm text-slate-500">Aucun appel enregistré sur les 30 derniers jours.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {appels.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 last:border-0">
                  <td className="whitespace-nowrap py-1.5 pr-3 text-slate-500">{dateCourte(a.at)}</td>
                  <td className="py-1.5 pr-3 font-mono text-[12px]">{a.operation}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3 text-right text-slate-500">
                    {a.duree_ms != null ? `${a.duree_ms} ms` : '—'}
                  </td>
                  <td className="whitespace-nowrap py-1.5 text-right">
                    <Badge className={a.resultat === 'ok'
                      ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {a.code || a.resultat}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  ));
}

function Volet({ volet }) {
  const sonde = pastille(SONDES, volet.sonde);
  const coeur = pastille(HEARTBEATS, volet.heartbeat.statut);
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`volet-${volet.service}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-400">{volet.service}</span>
        <Badge className={sonde.teinte} data-testid={`sonde-${volet.service}`}>{sonde.label}</Badge>
      </div>

      {/* Le heartbeat métier : la ligne qu'on vient lire. */}
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <Badge className={coeur.teinte} data-testid={`heartbeat-${volet.service}`}>{coeur.label}</Badge>
        <span className="text-sm text-slate-700">{volet.heartbeat.attendu}</span>
        <span className="text-xs text-slate-400">
          sur {fenetreLisible(volet.heartbeat.fenetre_heures)} · {depuis(volet.heartbeat.dernier)}
        </span>
      </div>
      {volet.heartbeat.precision && (
        <p className="mb-2 text-xs text-slate-500">{volet.heartbeat.precision}</p>
      )}

      {/* Nommer la variable absente, pas seulement colorer en rouge : c'est ce qui
          distingue « la clé manque » de « le partenaire est tombé ». */}
      {volet.variables_absentes.length > 0 && (
        <p className="mb-2 rounded bg-amber-50 px-2 py-1 font-mono text-xs text-amber-800"
           data-testid={`manque-${volet.service}`}>
          {volet.variables_absentes.join(' · ')} absente{volet.variables_absentes.length > 1 ? 's' : ''}
        </p>
      )}

      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-slate-400">Dernier succès</dt>
          <dd className="text-slate-700">{dateCourte(volet.dernier_succes?.at)}</dd>
        </div>
        <div>
          <dt className="text-slate-400">Dernier échec</dt>
          <dd className="text-slate-700">
            {dateCourte(volet.dernier_echec?.at)}
            {volet.dernier_echec?.detail && (
              <span className="block truncate text-red-600" title={volet.dernier_echec.detail}>
                {volet.dernier_echec.detail}
              </span>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export default function CarteIntegration({ carte }) {
  const [ouvert, setOuvert] = useState(false);
  const [journaux, setJournaux] = useState(null);
  const [chargement, setChargement] = useState(false);

  const basculer = async () => {
    const suivant = !ouvert;
    setOuvert(suivant);
    if (suivant && journaux === null) {
      setChargement(true);
      try { setJournaux((await getAppels(carte.cle)).journaux); }
      catch (e) { console.error(e); setJournaux([]); }
      setChargement(false);
    }
  };

  return (
    <Card data-testid={`carte-${carte.cle}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          {carte.nom}
        </CardTitle>
        <p className="text-xs text-slate-500">{carte.role}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {carte.volets.map((v) => <Volet key={v.service} volet={v} />)}

        <button
          onClick={basculer}
          data-testid={`appels-${carte.cle}`}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
        >
          {ouvert ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Les 20 derniers appels
        </button>
        {ouvert && (chargement
          ? <p className="text-sm text-slate-500">Chargement…</p>
          : <Appels journaux={journaux} />)}
      </CardContent>
    </Card>
  );
}
